import { roundToOneDecimal } from '../profileLogic'
import type { BaselineBenchmark, IntegrityBenchmark, OverallBenchmark, PeerValueCount } from './types'

const TOP_BADGE_MAX_PERCENT = 10

export enum KpiMetric {
  DisclosureScore = 'disclosureScore',
  Integrity = 'integrity',
  Baseline = 'baseline',
}

export enum Standing {
  Below = 'below',
  Average = 'average',
  Above = 'above',
}

export enum KpiBadgeKind {
  BelowAverage = 'belowAverage',
  Average = 'average',
  AboveAverage = 'aboveAverage',
  Top = 'top',
}

export interface KpiBadge {
  kind: KpiBadgeKind
  topPercent?: number
}

export interface PeerComparison {
  mean: number
  peerCount: number
  percentBelow?: number
  percentAbove?: number
}

export type KpiCardState =
  | { kind: 'notChecked'; comparison?: PeerComparison }
  | { kind: 'noBenchmark'; value: number }
  | { kind: 'compared'; value: number; comparison: PeerComparison; standing: Standing }

export interface KpiMessage {
  id: string
  values?: Record<string, string | number>
}

export enum BarTone {
  Positive = 'positive',
  Negative = 'negative',
  Warning = 'warning',
}

export interface KpiBar {
  fill: number
  tone: BarTone
  marker?: number
}

export function standingAgainst(value: number, mean: number): Standing {
  const shown = roundToOneDecimal(value)
  const shownMean = roundToOneDecimal(mean)
  if (shown < shownMean) return Standing.Below
  if (shown > shownMean) return Standing.Above
  return Standing.Average
}

export function resolveKpiCardState(value: number | undefined, comparison?: PeerComparison): KpiCardState {
  if (value === undefined) return { kind: 'notChecked', comparison }
  if (!comparison) return { kind: 'noBenchmark', value }
  return { kind: 'compared', value, comparison, standing: standingAgainst(value, comparison.mean) }
}

export function statusMessage(metric: KpiMetric, state: KpiCardState, filterLabel: string): KpiMessage | undefined {
  const metricMessage = (key: string) => `sustainabilityProfile.kpi.${metric}.${key}`

  if (state.kind === 'notChecked') return { id: metricMessage('notChecked') }
  if (state.kind === 'noBenchmark')
    return { id: 'sustainabilityProfile.kpi.unavailable', values: { filter: filterLabel } }

  if (state.value === 0) return { id: metricMessage('nothingFound') }
  return comparedMessage(metric, state.comparison, state.standing)
}

function comparedMessage(metric: KpiMetric, comparison: PeerComparison, standing: Standing): KpiMessage | undefined {
  if (standing === Standing.Average)
    return { id: 'sustainabilityProfile.kpi.inLine', values: { count: comparison.peerCount } }

  const percent = standing === Standing.Below ? comparison.percentAbove : comparison.percentBelow
  if (percent === undefined) return undefined
  return {
    id: `sustainabilityProfile.kpi.${metric}.${standing === Standing.Below ? 'lower' : 'higher'}`,
    values: { percent, count: comparison.peerCount },
  }
}

export function baselineStatusMessage(state: KpiCardState): KpiMessage | undefined {
  if (state.kind !== 'compared') return undefined
  return comparedMessage(KpiMetric.Baseline, state.comparison, state.standing)
}

export function disclosureBadge(state: KpiCardState): KpiBadge | undefined {
  if (state.kind !== 'compared') return undefined
  if (state.standing === Standing.Below) return { kind: KpiBadgeKind.BelowAverage }
  if (state.standing === Standing.Average) return { kind: KpiBadgeKind.Average }

  const { percentBelow } = state.comparison
  const topPercent = percentBelow === undefined ? undefined : Math.max(1, 100 - percentBelow)
  if (topPercent !== undefined && topPercent <= TOP_BADGE_MAX_PERCENT) return { kind: KpiBadgeKind.Top, topPercent }
  return { kind: KpiBadgeKind.AboveAverage }
}

export function disclosureComparison(overall?: OverallBenchmark): PeerComparison | undefined {
  if (overall?.scoreMean === undefined) return undefined
  return {
    mean: overall.scoreMean,
    peerCount: overall.peerCount,
    percentBelow: overall.percentBelow,
    percentAbove: overall.percentAbove,
  }
}

interface PeerDistribution {
  mean: number
  peerCount: number
  distribution: PeerValueCount[]
}

function percentOfPeers(peers: PeerDistribution, matches: (bucket: PeerValueCount) => boolean): number {
  const matching = peers.distribution.filter(matches).reduce((sum, bucket) => sum + bucket.peerCount, 0)
  return Math.round((100 * matching) / peers.peerCount)
}

const percentOfPeersBelow = (peers: PeerDistribution, value: number) =>
  percentOfPeers(peers, bucket => bucket.value < value)

const percentOfPeersAbove = (peers: PeerDistribution, value: number) =>
  percentOfPeers(peers, bucket => bucket.value > value)

function rankAgainst(value: number | undefined, peers: PeerDistribution): PeerComparison {
  const base = { mean: peers.mean, peerCount: peers.peerCount }
  if (value === undefined) return base
  return {
    ...base,
    percentBelow: percentOfPeersBelow(peers, value),
    percentAbove: percentOfPeersAbove(peers, value),
  }
}

export function integrityComparison(findings?: number, integrity?: IntegrityBenchmark): PeerComparison | undefined {
  if (!integrity || integrity.peerCount === 0) return undefined
  return rankAgainst(findings, integrity)
}

export function baselineComparison(level?: number, baseline?: BaselineBenchmark): PeerComparison | undefined {
  if (baseline?.overallMeanLevel === undefined || baseline.peerCount === 0) return undefined
  return rankAgainst(level, { ...baseline, mean: baseline.overallMeanLevel })
}

const TONE_BY_STANDING: Record<Standing, BarTone> = {
  [Standing.Below]: BarTone.Negative,
  [Standing.Average]: BarTone.Warning,
  [Standing.Above]: BarTone.Positive,
}

export function disclosureBar(state: KpiCardState): KpiBar | undefined {
  if (state.kind === 'notChecked') return undefined
  if (state.kind === 'noBenchmark') return { fill: state.value, tone: BarTone.Positive }
  return { fill: state.value, tone: TONE_BY_STANDING[state.standing], marker: state.comparison.mean }
}

export function integrityBar(state: KpiCardState, integrity?: IntegrityBenchmark): KpiBar | undefined {
  if (state.kind === 'notChecked') return undefined
  if (state.kind === 'noBenchmark' || !integrity) return { fill: state.value > 0 ? 100 : 0, tone: BarTone.Warning }
  return {
    fill: state.comparison.percentBelow ?? 0,
    tone: BarTone.Warning,
    marker: percentOfPeersBelow(integrity, integrity.mean),
  }
}
